/**
 * TailoringRemovalCard — sign-off card for `propose_tailoring_removal` (PLT-6322), the "undo own
 * insights" card.
 *
 * The undo counterpart to `<TailoringBatchCard>`. The chat-only tool validates a list of the company's
 * existing result tailorings and echoes it back; this card renders them as a checklist so the user
 * picks which to undo, then POSTs the selected `tailoringId`s to the batch-remove endpoint directly —
 * not back through the LLM — so the destructive step is deterministic.
 *
 * PLT-7899: each row is headed from the stored tailoring itself (`ownInsightRows`, read by the server
 * by id), in the same layout as the proposal card, and says what undoing it brings back. A card made
 * before the server read them falls back to the model's `summary`.
 *
 * Why the confirm step: clicking through destroys tailoring rows and queues a recalculation, so it
 * follows the proposal card's idle → confirming → removing → receipt machine, and a failure stays on
 * the editable phase because nothing was destroyed.
 *
 * Restricted-company handling: the list came from `get_dma_overrides`, which enforces per-variant read
 * access server-side, and the removal endpoint re-checks EDIT_CSRD_TAILORINGS plus that every id
 * belongs to this company's tailoring set. This card never re-fetches company data.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OwnInsightRemovalRow } from '@upright/common/mcp-payloads/own-insight-proposal';
import { JobStatus } from '@upright/common/internal/jobs/types';
import { BatchRemoveCsrdResultTailoringResponse } from '@upright/common/model/output/materiality/csrd-company-results/result-tailoring/api-types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { getIsJobComplete, useFetchJobStatus } from '@upright/ui/api/internal/jobs/use-fetch-job-status';
import { Button } from '@upright/ui/common/generic/components/button';
import { DataCard } from '@upright/ui/common/generic/components/card';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Link } from '@upright/ui/common/generic/components/link';
import { Spinner } from '@upright/ui/common/generic/components/spinner';
import { Heading, HeadingGroup, Paragraph, Text } from '@upright/ui/common/generic/components/typography';
import { ChatMarkdown } from './upright-chat-markdown';
import { ChangePart, OwnInsightRow, OwnInsightRows, matterLine, ownInsightHeadline } from './own-insight-row';
import styles from './own-insight-cards.module.css';

// ---------------------------------------------------------------------------
// Types — the shape the chat-only tool echoes back. Kept inline (not imported
// from the backend module) so the UI bundle does not pull in zod. Structurally
// matches `ProposeTailoringRemovalInput` in `mcp/tools/propose-tailoring-removal.ts`.
// ---------------------------------------------------------------------------

interface RemovalItem {
  tailoringId: number;
  summary: string;
  detail?: string;
  /**
   * Required in the tool schema, optional here on purpose: this card also renders the
   * streaming-preview payload, which reaches it as an unvalidated cast and can be missing fields
   * mid-stream. Typing it as always-present would make the fail-safe read below look redundant and
   * invite someone to delete it.
   */
  preChecked?: boolean;
}

export interface TailoringRemovalCardProps {
  companyId: string;
  /**
   * The company's variant (original or tailored). The backend resolves this to the tailored variant
   * via `getVariantTailoringLink`. When it is null the card disables Undo and explains why.
   */
  variantId: string | null;
  /**
   * Card identity, echoed by the tool. Undo sends it, so the server checks the removal against the
   * card it registered for this id (PLT-5064).
   */
  removalId: string;
  promptMarkdown?: string;
  items: RemovalItem[];
  /** PLT-7899: on the server result only. */
  ownInsightRows?: OwnInsightRemovalRow[];
}

type RemovalState =
  | { phase: 'idle' }
  | { phase: 'confirming' }
  | { phase: 'removing' }
  | { phase: 'removed'; result: BatchRemoveCsrdResultTailoringResponse }
  | { phase: 'kept' }
  | { phase: 'failed'; message: string };

/** What undoing an own insight brings back. */
const UNDO_EFFECT: Record<OwnInsightRemovalRow['kind'], string> = {
  own: 'Undoing removes it from your version',
  disabled: 'Undoing brings back Upright’s version',
  threshold: 'Undoing restores the previous threshold',
};

function changesCountLabel(count: number): string {
  return count === 1 ? '1 change' : `${count} changes`;
}

function formatAddedOn(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function TailoringRemovalCard({
  companyId,
  variantId,
  removalId,
  promptMarkdown,
  items,
  ownInsightRows,
}: TailoringRemovalCardProps) {
  // Anything that is not an explicit `true` starts UNSELECTED: a missing or malformed value in the
  // streaming preview must fail safe rather than pre-arm a deletion. The opposite default from
  // `<TailoringSelectionCard>`, where opting out of a non-destructive list is the right shape.
  const [selected, setSelected] = useState<Record<number, boolean>>(() => {
    const initial: Record<number, boolean> = {};
    for (const item of items) initial[item.tailoringId] = item.preChecked === true;
    return initial;
  });
  const [removalState, setRemovalState] = useState<RemovalState>({ phase: 'idle' });

  const rowsById = new Map((ownInsightRows ?? []).map(row => [row.tailoringId, row]));
  const selectedItems = items.filter(item => selected[item.tailoringId]);
  const selectedCount = selectedItems.length;
  const isConfirming = removalState.phase === 'confirming';
  const isRemoving = removalState.phase === 'removing';
  // Selection locks from the confirm step on, so the count confirmed is the count removed.
  const locked = isConfirming || isRemoving;

  const toggle = (tailoringId: number, value: boolean) => {
    if (locked) return;
    setSelected(prev => ({ ...prev, [tailoringId]: value }));
  };

  const setAll = (value: boolean) => {
    if (locked) return;
    setSelected(prev => {
      const next = { ...prev };
      for (const item of items) next[item.tailoringId] = value;
      return next;
    });
  };

  const queryClient = useQueryClient();

  const remove = async () => {
    if (selectedCount === 0 || !isConfirming || variantId === null) return;
    setRemovalState({ phase: 'removing' });
    try {
      const result = await fetchOrThrow<BatchRemoveCsrdResultTailoringResponse>(
        `/materiality/${encodeURIComponent(companyId)}/result-tailoring-batch-remove/${encodeURIComponent(variantId)}`,
        'POST',
        { tailoringIds: selectedItems.map(item => item.tailoringId), removalId },
      );
      setRemovalState({ phase: 'removed', result });
      // The tailoring rows are gone now; the recompute runs async and is followed by the receipt.
      void queryClient.invalidateQueries({ queryKey: ['materiality'] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'The changes could not be undone.';
      setRemovalState({ phase: 'failed', message });
    }
  };

  if (removalState.phase === 'removed') {
    return <TailoringRemovalReceipt result={removalState.result} items={selectedItems} rowsById={rowsById} />;
  }

  if (removalState.phase === 'kept') {
    return (
      <DataCard>
        <Text size="sm" height="xs" color="gray-600">
          Nothing was undone. Your own insights stay as they are.
        </Text>
      </DataCard>
    );
  }

  return (
    <DataCard>
      <Stack gap="lg">
        <HeadingGroup>
          <Heading level={3} size="lg" height="lg" weight="bold" color="gray-900">
            Undo own insights
          </Heading>
          <Paragraph size="sm" height="xs" color="gray-600">{`${selectedCount} of ${items.length} selected`}</Paragraph>
        </HeadingGroup>
        {promptMarkdown && promptMarkdown.length > 0 && <ChatMarkdown>{promptMarkdown}</ChatMarkdown>}
        <Stack gap="sm">
          {!locked && items.length > 1 && (
            <div className={styles.groupHead}>
              <span className={styles.groupActions}>
                <Text size="sm" height="xs">
                  <Link
                    onPress={() => {
                      setAll(true);
                    }}
                  >
                    Select all
                  </Link>
                </Text>
                <Text size="sm" height="xs">
                  <Link
                    onPress={() => {
                      setAll(false);
                    }}
                  >
                    Clear
                  </Link>
                </Text>
              </span>
            </div>
          )}
          <OwnInsightRows>
            {items.map(item => {
              const row = rowsById.get(item.tailoringId);
              return (
                <OwnInsightRow
                  key={item.tailoringId}
                  headline={row ? ownInsightHeadline(row, item.summary) : item.summary}
                  meta={
                    row ? matterLine(row.matterName, `added ${formatAddedOn(row.addedAt)}`) : (item.detail ?? undefined)
                  }
                  change={row ? <ChangePart>{UNDO_EFFECT[row.kind]}</ChangePart> : undefined}
                  selection={{
                    isSelected: !!selected[item.tailoringId],
                    isDisabled: locked,
                    onChange: value => {
                      toggle(item.tailoringId, value);
                    },
                  }}
                />
              );
            })}
          </OwnInsightRows>
        </Stack>
        {isConfirming ? (
          <div className={styles.commit} role="group" aria-label="Confirm undo">
            <Text
              size="md"
              height="sm"
              weight="semibold"
              color="gray-900"
            >{`Undo ${changesCountLabel(selectedCount)}?`}</Text>
            <Text size="sm" height="xs" color="gray-800">
              The tailored version is recalculated, which takes about a minute. To make a change again, ask in the chat
              or add it on the company’s DMA page.
            </Text>
            <Stack row gap="sm" align="center" wrap>
              <Button
                variant="outlined"
                size="small"
                onPress={() => {
                  setRemovalState({ phase: 'idle' });
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="small"
                onPress={() => {
                  void remove();
                }}
              >
                {`Undo ${changesCountLabel(selectedCount)}`}
              </Button>
            </Stack>
          </div>
        ) : (
          // Secondary action first, primary last.
          <Stack row gap="sm" align="center" wrap>
            <Button
              variant="outlined"
              size="small"
              isDisabled={isRemoving}
              onPress={() => {
                setRemovalState({ phase: 'kept' });
              }}
            >
              Keep all
            </Button>
            <Button
              variant="contained"
              size="small"
              isDisabled={selectedCount === 0 || isRemoving || variantId === null}
              onPress={() => {
                setRemovalState({ phase: 'confirming' });
              }}
            >
              {isRemoving ? 'Undoing…' : `Undo ${changesCountLabel(selectedCount)}`}
            </Button>
          </Stack>
        )}
        {removalState.phase === 'failed' && (
          <div role="alert">
            <Text size="sm" height="xs" color="gray-800">
              {removalState.message}
            </Text>
          </div>
        )}
        {variantId === null && (
          <Text size="sm" height="xs" color="gray-600">
            Own insights can be undone only for a company whose tailored version you can edit.
          </Text>
        )}
      </Stack>
    </DataCard>
  );
}

function TailoringRemovalReceipt({
  result,
  items,
  rowsById,
}: {
  result: BatchRemoveCsrdResultTailoringResponse;
  items: RemovalItem[];
  rowsById: Map<number, OwnInsightRemovalRow>;
}) {
  const removedIds = new Set(result.removedTailoringIds);
  const removedItems = items.filter(item => removedIds.has(item.tailoringId));
  const removedCount = result.removedTailoringIds.length;
  const missingCount = result.missingTailoringIds.length;

  // The removal queues one recompute, so the write returns before the results change. Poll the exact
  // job; its id is absent when nothing was removed (every id already gone).
  const recomputeJobId = result.recomputeJobId;
  const statusQuery = useFetchJobStatus(recomputeJobId);
  const isRecomputing =
    recomputeJobId !== undefined && (!statusQuery.data || !getIsJobComplete(statusQuery.data.status));
  const hasRecomputeFailed = statusQuery.data?.status === JobStatus.ERROR;

  const queryClient = useQueryClient();
  const hasInvalidated = useRef(false);
  useEffect(() => {
    if (recomputeJobId !== undefined && !isRecomputing && !hasInvalidated.current) {
      hasInvalidated.current = true;
      void queryClient.invalidateQueries({ queryKey: ['materiality'] });
    }
  }, [recomputeJobId, isRecomputing, queryClient]);

  const status =
    removedCount === 0
      ? 'These changes were already undone.'
      : hasRecomputeFailed
        ? 'Undone, but recalculating the tailored version failed, so its results may still show the old values.'
        : isRecomputing
          ? undefined
          : 'Tailored version recalculated.';

  return (
    <DataCard>
      <Stack gap="lg">
        <HeadingGroup>
          <Heading level={3} size="lg" height="lg" weight="bold" color="gray-900">
            {removedCount === 0 ? 'Nothing left to undo' : `${changesCountLabel(removedCount)} undone`}
          </Heading>
          {/* Already-gone ids are reported, not treated as an error: the end state is the one asked for. */}
          {missingCount > 0 && removedCount > 0 && (
            <Paragraph size="sm" height="xs" color="gray-600">
              {`${missingCount} already undone`}
            </Paragraph>
          )}
        </HeadingGroup>
        {isRecomputing && !hasRecomputeFailed ? (
          <div className={styles.status}>
            <Spinner size="small" />
            <Text size="sm" height="xs" color="gray-800">
              Recalculating the tailored version. This takes about a minute.
            </Text>
          </div>
        ) : (
          status && (
            <Text size="sm" height="xs" color="gray-800">
              {status}
            </Text>
          )
        )}
        {removedItems.length > 0 && (
          <OwnInsightRows>
            {removedItems.map(item => {
              const row = rowsById.get(item.tailoringId);
              return (
                <OwnInsightRow
                  key={item.tailoringId}
                  headline={row ? ownInsightHeadline(row, item.summary) : item.summary}
                  meta={row ? matterLine(row.matterName) : undefined}
                />
              );
            })}
          </OwnInsightRows>
        )}
      </Stack>
    </DataCard>
  );
}
